<template>
  <main-layout
    bg-color="bg-primary-600"
  >
    <template #nav>
      <div class="flex flex-wrap items-center justify-between mx-auto">
        <h1 class="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Discover</h1>
        <router-link
          :to="{
            name: 'Basket',
            query: {
              token: Jwt,
            },
          }"
          class="relative inline-flex items-center p-1.5 text-sm font-medium text-center rounded-full border-2 border-secondary-600 focus:ring-0 focus:outline-none focus:ring-primary-600"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-secondary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 11 4-7" /><path d="m19 11-4-7" /><path d="M2 11h20" /><path d="m3.5 11 1.6 7.4a2 2 0 0 0 2 1.6h9.8c.9 0 1.8-.7 2-1.6l1.7-7.4" /><path d="m9 11 1 9" /><path d="M4.5 15.5h15" /><path d="m15 11-1 9" /></svg>
          <span class="sr-only">Total basket items</span>
          <div class="absolute inline-flex items-center justify-center h-4 w-4 font-bold text-primary-600 bg-secondary-600 rounded-md -top-1 -right-2" style="font-size: 8px;">{{ TotalBasketItems }}</div>
        </router-link>
      </div>
    </template>
    <div class="px-0">
      <slot v-if="isLoading">
        loading
      </slot>
      <slot v-else>
        <greeting-card v-if="CustomerName" />
        <search-input class="rounded-b-md sticky top-[44px] h-fit relative z-[1000] px-4 pt-2 pb-4" />
        <div
          v-if="ProductCategories.length"
          class="bg-white"
        >
          <category-filter class="p-2" />
        </div>

        <div class="p-2 mt-0">
          <h2 class="font-bold text-sm text-gray-800">
            Shoppers Like You Also Bought
          </h2>
        </div>

        <div class="overflow-x-auto">
          <div class="flex space-x-4 px-3 pb-3">
            <slot
              v-for="product in filteredRecommendations"
              :key="product.id"
            >
              <div
                v-if="product.outOfStock"
                class="focus:outline-none focus:ring-0 text-left p-2 border-2 border-secondary-600 rounded-md flex-shrink-0 w-[70%] sm:w-[45%] md:w-[30%] lg:w-[22%] min-w-[153px]"
              >
                <product-card
                  :currency="Currency"
                  :id="product.id"
                  :image-url="product.imageUrl"
                  :name="product.name"
                  :price="+product.price"
                />
              </div>
              <button
                v-else
                type="button"
                @click="toProductDetail(product.id)"
                class="focus:outline-none focus:ring-0 text-left p-3 border-2 border-secondary-600 rounded-md flex-shrink-0 w-[70%] sm:w-[45%] md:w-[30%] lg:w-[22%] min-w-[153px]"
              >
                <product-card
                  :currency="Currency"
                  :id="product.id"
                  :image-url="product.imageUrl"
                  :name="product.name"
                  :price="+product.price"
                  :out-of-stock="!!product.outOfStock"
                />
              </button>
            </slot>
          </div>
        </div>

        <div
          class="grid grid-cols-2 gap-4 px-4 mt-4"
        >
          <slot
            v-for="product in UniqueProducts"
            :key="product.id"
          >
            <div
              class="focus:outline-none focus:ring-0 text-left items-stretch p-4 border-2 border-secondary-600 rounded-md w-full"
              v-if="product.outOfStock"
            >
              <product-card
                :currency="Currency"
                :id="product.id"
                :image-url="product.imageUrl"
                :name="product.name"
                :price="product.price"
                :out-of-stock="product.outOfStock"
              />
            </div>
            <button
              type="button"
              @click="toProductDetail(product.id)"
              class="focus:outline-none focus:ring-0 text-left items-stretch p-4 border-2 border-secondary-600 rounded-md w-full"
              v-else
            >
              <product-card
                :currency="Currency"
                :id="product.id"
                :image-url="product.imageUrl"
                :name="product.name"
                :price="product.price"
                :out-of-stock="!!product.outOfStock"
              />
            </button>
          </slot>
        </div>
      </slot>
      <div
        class="px-4"
      >
        <no-data
          class="mt-4"
          message="Your search did not return any results"
          title="No products"
          v-if="!IsLoadingProducts && !Products.length"
        >
          <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="flex-shrink-0 w-10 h-10 text-secondary-600 transition duration-75 group-hover:text-gray-900">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
            </svg>
          </template>
        </no-data>
      </div>
    </div>
  </main-layout>

</template>
<script setup>

import {
  computed, onMounted, onUnmounted,
} from 'vue';
import { useProductsStore } from '@/stores/products';
import { useProductCategoriesStore } from '@/stores/productCategories';

import { useBasketStore } from '@/stores/basket';
import { useAccountStore } from '@/stores/account';
import { useRouter } from 'vue-router';
import { ALL_FILTERS } from '@/constants';

import MainLayout from '@/layouts/MainLayout.vue';
import CategoryFilter from '@/components/products/CategoryFilter.vue';
import ProductCard from '@/components/products/ProductCard.vue';
import SearchInput from '@/components/products/SearchInput.vue';
import GreetingCard from '@/components/products/GreetingCard.vue';
import NoData from '@/components/shared/NoData.vue';
import { useProductRecommendationStore } from '@/stores/productRecommendation';

const IsLoadingProducts = computed(() => useProductsStore().IsLoading);
const IsLoadingProductProductCategories = computed(() => useProductCategoriesStore().IsLoading);
const Products = computed(() => useProductsStore().FilteredData);
const ProductCategories = computed(() => useProductCategoriesStore().Data);
const productRecommendationStore = useProductRecommendationStore();
const recommendations = computed(() => productRecommendationStore.Data);

const Currency = computed(() => useAccountStore().Currency);
const CustomerNames = computed(() => useAccountStore().CustomerNames);
const TotalBasketItems = computed(() => useBasketStore().TotalItems);
const Jwt = computed(() => useAccountStore().Jwt);

const isLoading = computed(() => IsLoadingProducts.value
  || IsLoadingProductProductCategories.value);

const router = useRouter();

const {
  Load: LoadProductCategories,
} = useProductCategoriesStore();

const {
  SetCategoryFilter,
  SetSearchTextFilter,
} = useProductsStore();

const CustomerName = computed(() => {
  if (!CustomerNames.value?.length) return null;
  return CustomerNames.value.split(' ')[0];
});

const UniqueProducts = computed(() => {
  const seen = new Set();
  return Products.value.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
});

const filteredRecommendations = computed(() => recommendations.value
  .map((r) => Products.value.find((p) => p.id === r.productId))
  .filter((p) => p && !p.outOfStock));

const toProductDetail = (id) => {
  router.replace({
    name: 'Product Detail',
    query: {
      token: Jwt.value,
    },
    params: {
      id,
    },
  });
};

onMounted(() => {
  if (!ProductCategories.value.length) {
    LoadProductCategories();
  }
  if (!recommendations.value.length) {
    productRecommendationStore.Load();
  }
});
onUnmounted(() => {
  SetCategoryFilter(ALL_FILTERS.value);
  SetSearchTextFilter(null);
});
</script>
